/**
 * Every tuning value in the game lives in this file: car physics, surfaces,
 * steering, circuit generation, the bot driver, medals and the chase camera.
 *
 * PHYSICS is part of the game's identity: every player drives the same car on a
 * given day, and recorded laps only replay correctly with the exact same
 * numbers. Changing a value changes that era's physics hash, which marks older
 * lap records as incompatible.
 * The dev panel (F1) edits a live copy; laps driven with edited values are
 * never saved as records.
 */

import type { EraId } from './eras';

export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;
/**
 * Bump whenever code in sim/ changes how the car behaves. It is part of every
 * record's physics hash, so laps driven on older code are retired instead of
 * failing to replay. 2: the steering lock and the stability assist count
 * downforce. 3: the lock's overdrive no longer grows with aero balance.
 * 4: hills (slope gravity, load in dips and over crests).
 */
export const SIM_VERSION = 4;

export interface PhysicsTuning {
  // --- chassis
  mass: number;
  yawInertia: number;
  cgToFront: number;
  cgToRear: number;
  cgHeight: number;
  halfTrackFront: number;
  halfTrackRear: number;
  carHalfWidth: number;
  substeps: number;
  // --- tires
  tireMu: number;
  frontGrip: number;
  rearGrip: number;
  peakSlipFront: number;
  peakSlipRear: number;
  falloffFront: number;
  falloffRear: number;
  lockGrip: number;
  /** Lateral grip left on a locked axle: locked fronts barely steer. */
  lockLateral: number;
  wheelspinGrip: number;
  /** >1 leaves more lateral grip available while braking/accelerating. */
  frictionEllipse: number;
  lowSpeedBlend: number;
  // --- engine & gearbox
  enginePeakTorque: number;
  redlineRpm: number;
  idleRpm: number;
  gear1: number;
  gear2: number;
  gear3: number;
  gear4: number;
  gear5: number;
  finalDrive: number;
  rearWheelRadius: number;
  drivetrainEfficiency: number;
  shiftUpRpm: number;
  shiftDownRpm: number;
  // --- brakes
  brakeForce: number;
  brakeBias: number;
  /** Pedal response, units per second (pressure builds, the engine responds). */
  pedalRise: number;
  pedalRelease: number;
  // --- aero: load per (m/s)^2 on each axle; keeps the car planted at speed
  downforceFront: number;
  downforceRear: number;
  // --- resistances
  dragCoef: number;
  rollingResistance: number;
  // --- steering
  steerRate: number;
  steerReturnRate: number;
  maxSteerAngle: number;
  /** Full lock = this x the steering the tightest grip-limited line needs... */
  steerLimitMargin: number;
  /** ...plus this much extra wheel angle (rad), enough to overdrive the front tires. */
  steerOverdrive: number;
  // --- surfaces
  curbGrip: number;
  curbDrag: number;
  grassGrip: number;
  grassDrag: number;
  gravelGrip: number;
  gravelDrag: number;
  // --- barriers at the edge of the runoff
  barrierRestitution: number;
  barrierFriction: number;
  // --- driver aids (the same for everyone; 0 switches one off)
  /** 0..1: trims the throttle before the rear tires spin up... */
  tractionControl: number;
  /** ...keeping the drive at this fraction of the rear's longitudinal grip... */
  tcTarget: number;
  /** ...and less of it the harder the rear is cornering (0 = ignore cornering; 1 = hold the line). */
  tcCornering: number;
  /** 0..1: trims the brakes before a wheel locks... */
  abs: number;
  /** ...keeping braking at this fraction of each axle's grip. */
  absTarget: number;
  /** 1/s: damps rotation beyond what the steering asks for, so slides recover. */
  yawAssist: number;
  /** Max body slip (rad, ~tan) before the car is pulled straight again. */
  slipAssistAngle: number;
  /**
   * 0..1 (cars from HANDLING_3_FROM only, absent before so older laps keep their fingerprint): the ABS
   * leaves the front tyres more of their grip for turning the more steering is held, so braking into a corner
   * still steers (at full lock the brakes get sqrt(1 - x^2) of the front's grip).
   */
  absCornering?: number;
  /**
   * m/s (cars from HANDLING_31_FROM): the steering-aware ABS fades out between 15 m/s under this and this, so
   * braking hard from speed with the steering held (a keyboard's full lock) stops the car as it always did.
   */
  absCorneringTop?: number;
  /**
   * Ground effect's yaw sensitivity (cars from HANDLING_33_FROM): sideways, the downforce falls to
   * 1 - aeroYawLoss × tan(slip)² of itself (never under a fifth). A slide at speed loses the grip it slides on.
   */
  aeroYawLoss?: number;
  /**
   * Stability control's cost (cars from HANDLING_33_FROM): straightening a car that rotates faster than its tyres
   * allow brakes it by escBrake m/s² per rad/s of excess rotation, at most escMaxDecel m/s².
   */
  escBrake?: number;
  escMaxDecel?: number;
  /** 1 (cars from HANDLING_33_FROM): the slip limiter turns the nose to the path instead of bending the path. */
  slipAssistStraighten?: number;
  /**
   * Max body slip while braking (cars from HANDLING_35_FROM, with slipAssistStraighten): the slip limiter's angle
   * closes from slipAssistAngle to this as the brake pedal goes down, and it straightens twice as fast. Braking, the
   * car stays pointed where it's going (a tap mid-corner still tucks the nose in).
   */
  slipAssistBraking?: number;
  /**
   * Cars from HANDLING_35_FROM: the ABS lets less brake through a sliding tyre, from its peak slip angle to
   * 1 + 1/absSliding times it, where it keeps absSlidingFloor of its brake (none below that).
   */
  absSliding?: number;
  absSlidingFloor?: number;
}

export const DEFAULT_PHYSICS: Readonly<PhysicsTuning> = Object.freeze({
  mass: 640,
  yawInertia: 820,
  cgToFront: 1.25,
  cgToRear: 1.1,
  cgHeight: 0.27,
  halfTrackFront: 0.72,
  halfTrackRear: 0.8,
  carHalfWidth: 0.95,
  substeps: 4,

  tireMu: 1.55,
  frontGrip: 0.94,
  rearGrip: 1.04,
  peakSlipFront: 0.14,
  peakSlipRear: 0.11,
  falloffFront: 0.1,
  falloffRear: 0.05,
  lockGrip: 0.72,
  lockLateral: 0.5,
  wheelspinGrip: 0.9,
  frictionEllipse: 1.25,
  lowSpeedBlend: 3.0,

  enginePeakTorque: 245,
  redlineRpm: 11000,
  idleRpm: 2800,
  gear1: 3.2,
  gear2: 2.36,
  gear3: 1.86,
  gear4: 1.53,
  gear5: 1.3,
  finalDrive: 3.9,
  rearWheelRadius: 0.34,
  drivetrainEfficiency: 0.9,
  shiftUpRpm: 10700,
  shiftDownRpm: 6000,

  brakeForce: 8200,
  brakeBias: 0.72,
  pedalRise: 9,
  pedalRelease: 14,

  downforceFront: 0.09,
  downforceRear: 0.24,

  dragCoef: 0.56,
  rollingResistance: 11,

  steerRate: 5.5,
  steerReturnRate: 9,
  maxSteerAngle: 0.52,
  steerLimitMargin: 1.25,
  steerOverdrive: 0.045,

  curbGrip: 0.97,
  curbDrag: 0.15,
  grassGrip: 0.8,
  grassDrag: 1.2,
  gravelGrip: 0.65,
  gravelDrag: 3.4,

  barrierRestitution: 0.08,
  barrierFriction: 0.12,

  tractionControl: 1,
  tcTarget: 0.82,
  tcCornering: 0.85,
  abs: 1,
  absTarget: 0.96,
  yawAssist: 6,
  slipAssistAngle: 0.3,
});

/**
 * Each day's era car is DEFAULT_PHYSICS (the 1961 car) plus these overrides.
 * Every era keeps the driver aids on: they feel different, none are hard.
 */
export const ERA_PHYSICS: Record<EraId, Partial<PhysicsTuning>> = {
  // Supercharged straight-eight, skinny tyres, drum brakes: slower, slidey, forgiving.
  '1932': {
    mass: 760,
    yawInertia: 980,
    tireMu: 1.3,
    frontGrip: 0.95,
    rearGrip: 1.02,
    peakSlipRear: 0.12,
    falloffRear: 0.07,
    enginePeakTorque: 340,
    redlineRpm: 6200,
    idleRpm: 1500,
    shiftUpRpm: 6000,
    shiftDownRpm: 3400,
    finalDrive: 3.05,
    brakeForce: 6400,
    downforceFront: 0,
    downforceRear: 0,
    dragCoef: 0.66,
    steerRate: 5,
  },
  // Supercharged V12 in a streamlined body: huge top speed, modest grip.
  '1937': {
    mass: 740,
    yawInertia: 960,
    tireMu: 1.34,
    frontGrip: 0.94,
    rearGrip: 1.03,
    falloffRear: 0.07,
    enginePeakTorque: 420,
    redlineRpm: 7600,
    idleRpm: 1800,
    shiftUpRpm: 7400,
    shiftDownRpm: 4200,
    finalDrive: 2.78,
    brakeForce: 6800,
    downforceFront: 0,
    downforceRear: 0.02,
    dragCoef: 0.42,
  },
  // Front-engined straight-six: a touch more weight on the nose.
  '1954': {
    mass: 680,
    yawInertia: 900,
    cgToFront: 1.15,
    cgToRear: 1.2,
    tireMu: 1.45,
    enginePeakTorque: 280,
    redlineRpm: 8200,
    idleRpm: 2200,
    shiftUpRpm: 8000,
    shiftDownRpm: 4600,
    finalDrive: 3.37,
    brakeForce: 7400,
    downforceFront: 0.02,
    downforceRear: 0.04,
    dragCoef: 0.55,
  },
  // The reference car.
  '1961': {},
  // Wings and slicks: grip and downforce.
  '1972': {
    mass: 600,
    yawInertia: 760,
    tireMu: 1.64,
    frontGrip: 0.95,
    rearGrip: 1.06,
    peakSlipRear: 0.1,
    enginePeakTorque: 285,
    redlineRpm: 10500,
    shiftUpRpm: 10300,
    shiftDownRpm: 6000,
    finalDrive: 3.9,
    brakeForce: 9000,
    downforceFront: 0.18,
    downforceRear: 0.4,
    dragCoef: 0.72,
  },
  // Turbo: big power once the boost builds (a slower throttle), lots of downforce.
  '1985': {
    mass: 580,
    yawInertia: 720,
    tireMu: 1.66,
    frontGrip: 0.95,
    rearGrip: 1.06,
    enginePeakTorque: 420,
    redlineRpm: 11500,
    shiftUpRpm: 11300,
    shiftDownRpm: 7000,
    finalDrive: 3.79,
    brakeForce: 9600,
    pedalRise: 4.5,
    downforceFront: 0.18,
    downforceRear: 0.42,
    dragCoef: 0.7,
  },
  // Screaming V10, carbon brakes, the most grip of all.
  '1994': {
    mass: 560,
    yawInertia: 700,
    tireMu: 1.74,
    frontGrip: 0.96,
    rearGrip: 1.06,
    enginePeakTorque: 320,
    redlineRpm: 14500,
    idleRpm: 4000,
    shiftUpRpm: 14200,
    shiftDownRpm: 9000,
    finalDrive: 4.62,
    brakeForce: 10500,
    downforceFront: 0.24,
    downforceRear: 0.52,
    dragCoef: 0.68,
    steerRate: 6,
  },
  // The V10 at its peak: the lightest, sharpest car, revving to 18,500.
  '2004': {
    mass: 550,
    yawInertia: 720,
    tireMu: 1.8,
    frontGrip: 0.96,
    rearGrip: 1.06,
    enginePeakTorque: 370,
    redlineRpm: 18500,
    idleRpm: 5000,
    shiftUpRpm: 18200,
    shiftDownRpm: 11500,
    finalDrive: 5.6,
    brakeForce: 11000,
    downforceFront: 0.3,
    downforceRear: 0.6,
    dragCoef: 0.7,
  },
  // Ground effect: long and heavy (lazier to turn in), hybrid torque, the most downforce.
  '2022': {
    mass: 800,
    yawInertia: 1100,
    cgToFront: 1.38,
    cgToRear: 1.22,
    tireMu: 1.84,
    frontGrip: 0.96,
    rearGrip: 1.06,
    enginePeakTorque: 600,
    redlineRpm: 12500,
    idleRpm: 4500,
    shiftUpRpm: 12200,
    shiftDownRpm: 8000,
    finalDrive: 3.75,
    brakeForce: 13500,
    downforceFront: 0.46,
    downforceRear: 0.92,
    dragCoef: 0.86,
  },
};

/**
 * The handling pass of 29 September 2026, built from the setup that felt best
 * on the 1994 car: a touch more grip, the aero balance moved forward (more
 * front downforce: sharper turn-in), and brake bias moved rearward (the car
 * rotates as you brake into a corner instead of ploughing on). Scaled by era:
 * the wingless cars keep (almost) no downforce, newer winged cars get more.
 * Earlier days keep the handling their laps were set with.
 */
export const HANDLING_2_FROM = '2026-09-29';
const ERA_PHYSICS_2: Record<EraId, Partial<PhysicsTuning>> = {
  '1932': { frontGrip: 0.97, rearGrip: 1.04, brakeBias: 0.67 },
  '1937': { frontGrip: 0.96, rearGrip: 1.05, brakeBias: 0.67 },
  '1954': { frontGrip: 0.96, rearGrip: 1.06, downforceFront: 0.03, brakeBias: 0.67 },
  '1961': { frontGrip: 0.96, rearGrip: 1.06, downforceFront: 0.12, brakeBias: 0.67 },
  '1972': { frontGrip: 0.97, rearGrip: 1.08, downforceFront: 0.25, downforceRear: 0.44, brakeBias: 0.67 },
  '1985': { frontGrip: 0.97, rearGrip: 1.08, downforceFront: 0.26, downforceRear: 0.47, brakeBias: 0.68 },
  // The reference: exactly the setup that felt great.
  '1994': { frontGrip: 0.98, rearGrip: 1.08, downforceFront: 0.34, downforceRear: 0.6, brakeBias: 0.69 },
  // The most agile car of all: the quickest to turn in (light on its feet, a quick rack), grip between
  // the 1994 and the 2022. A touch more front brake, or hard braking into a turn spins it.
  // (A sharper version, rack 8.5 and inertia 510 with more lock, was "way too agile".)
  '2004': {
    steerRate: 7,
    yawInertia: 580,
    tireMu: 1.9,
    frontGrip: 0.98,
    rearGrip: 1.08,
    downforceFront: 0.48,
    downforceRear: 0.76,
    brakeBias: 0.73,
  },
  // The best car of all, everywhere: lighter and sharper than before, the grippiest tyres and the most downforce.
  '2022': {
    mass: 740,
    yawInertia: 860,
    steerRate: 6.5,
    tireMu: 1.94,
    frontGrip: 0.98,
    rearGrip: 1.08,
    downforceFront: 0.74,
    downforceRear: 1.2,
    brakeBias: 0.69,
  },
};

/**
 * The handling pass of 7 October 2026, after the first week of public players ("I can't even get through the
 * first corner", "steering right and braking at the same time feels off", the 1932 car "understeering like it
 * has no business to"). Every car: the more steering you hold, the less brake pressure the ABS lets through
 * (both axles, so the balance stays put), so braking into a corner turns instead of ploughing on: the 2004 car
 * braking from 80 km/h at full lock turned 1 degree in a second, now 48 (it sheds a little less speed while it
 * turns, as a real car does), and every car slides less doing it. The three oldest cars: more front grip (they
 * turn in rather than push wide), stronger drums (the first corner after a long straight is less of a cliff)
 * and a quicker rack. The stability assists stay as they are: easing them made new players crash more.
 * Earlier days keep the handling their laps were set with. It came in during the 6 October race: that day's
 * laps from before it still count on the day's board (see earlierPhysics, BOARD_KEYS).
 */
export const HANDLING_3_FROM = '2026-10-06';
const HANDLING_3_ALL: Partial<PhysicsTuning> = { absCornering: 0.8 };
const ERA_PHYSICS_3: Partial<Record<EraId, Partial<PhysicsTuning>>> = {
  '1932': { frontGrip: 1.0, brakeForce: 7200, steerRate: 6 },
  '1937': { frontGrip: 0.99, brakeForce: 7600, steerRate: 6 },
  '1954': { frontGrip: 0.99, brakeForce: 8200, steerRate: 6 },
};

/**
 * Handling 3.1 (7 October 2026): the steering-aware ABS only where braking used to plough on, at the lower
 * speeds, fading out by 126 km/h. At full strength from any speed, holding the steering (on keys, full lock)
 * while braking from the end of a straight let through so little brake that the lesson's "brake now" came
 * too late for turn 1; braking from speed with the steering held now stops the car as it did before 3.
 */
export const HANDLING_31_FROM = '2026-10-07';
const HANDLING_31_ALL: Partial<PhysicsTuning> = { absCorneringTop: 35 };

/**
 * Handling 3.2 (9 October 2026): the 2022 car with more front downforce (0.74 to 0.95), the setup that felt much
 * better on the day's circuit in the dev panel: it turns in sharper at speed. Earlier days keep the handling their
 * laps were set with (7 October's race was a 2022 day).
 */
export const HANDLING_32_FROM = '2026-10-09';
const ERA_PHYSICS_32: Partial<Record<EraId, Partial<PhysicsTuning>>> = {
  '2022': { downforceFront: 0.95 },
};

/**
 * Handling 3.3 (from the next 2022 day, 14 October 2026): the 2022 car rebuilt so that braking in a straight line
 * and turning in at the edge of the tyres is the fast way round, not drifting ("drifting shouldn't really be a
 * thing"). Its top laps were thrown sideways into the slow corners (past 20 degrees on nearly every lap on the board)
 * because a slide cost nothing and paid twice: its brakes were far weaker than its grip, so sliding sideways was the
 * hardest way to slow down; and the slip limiter bent a sliding car's path round the corner for nothing (about 2 g).
 * Now, as on a real ground-effect car:
 * - brakes much nearer the grip (20 kN, a touch more to the front: 280 to 100 km/h in 86 m, from 118; 24 kN was
 *   "too strong"), so the straight-line stop is the big one;
 * - downforce that falls away when the car is sideways (the floor loses its seal);
 * - a stability control that brakes to straighten a slide, costing a little speed instead of nothing;
 * - a slip limiter that turns the nose to the path instead of pulling the path round.
 * A corner lab (a line-following driver whose braking and corner speed are searched for the fastest way through each
 * of 9 October's four slow corners): drifting was 0.1 to 0.55 s faster per corner, now 0.07 to 0.25 s slower, and the
 * clean way is as quick as before. Bench beginners on 2022 days do better than before (late brakers on keys: 50 clean
 * laps of 66, from 30; no spins). Tyres that give up more grip past the peak were tried and dropped: they crashed
 * beginners twice as often. Straightening alone and a slide costing speed hard ("it just stops dead"), and a spin
 * above a set speed ("a triggered spin"), were tried on staging before this.
 */
export const HANDLING_33_FROM = '2026-10-14';
const ERA_PHYSICS_33: Partial<Record<EraId, Partial<PhysicsTuning>>> = {
  '2022': { brakeForce: 20000, brakeBias: 0.73, aeroYawLoss: 4, escBrake: 10, escMaxDecel: 5, slipAssistStraighten: 1 },
};

/**
 * Handling 3.4 (12 October 2026, from the first minutes of that day's race): the 2004 car gets the 2022's rebuild
 * (handling 3.3), so braking in a straight line and turning in at the edge of the tyres is the fast way round, not
 * throwing it sideways. A corner lab on the day's circuit (Malgasasso, five slow corners) found the same fault: a
 * drift was 0.13 to 0.55 s quicker through four of them. Now it's 0.07 to 0.25 s slower where a drift gets through at
 * all, and the clean way is as quick as before. Its brakes come up to 16.5 kN (280 to 100 km/h in 78 m, from 107), the strongest that
 * bench beginners liked best: on twelve 2004 days, 149 clean laps of 360 against 132, and no spins (15 and 15.5 kN
 * each spun three times). It came in during the race, so its laps go on the board the day began with (BOARD_KEYS);
 * the one lap already set with the old car (2 s clear of the rest) was taken off it, and the old car no longer counts
 * that day: everyone races the same car (a note as the first race sets off says so, game/carNotes.ts).
 */
export const HANDLING_34_FROM = '2026-10-12';
const ERA_PHYSICS_34: Partial<Record<EraId, Partial<PhysicsTuning>>> = {
  '2004': { brakeForce: 16500, aeroYawLoss: 4, escBrake: 10, escMaxDecel: 5, slipAssistStraighten: 1 },
};

/**
 * Handling 3.5 (from 14 October 2026, the 2022's first day as a grip car; the 2004 from its next day): braking while
 * turning no longer throws the car sideways. Players: braking and turning, the 2004 went "sideways back the way it
 * came… like driving a crab or a very strong crosswind". Their repro, on open tarmac (150 km/h, full brake, half
 * steering): the weight on the nose let the rear step out, the car turned 21 degrees into the corner while still going
 * straight, and its brakes, pushing along the nose, then shoved it the other way (nearly 40 degrees against the
 * steering by the time it stopped; a quarter of steering was enough). Now:
 * - braking, the slip limiter holds the car to 7 degrees of where it's going (from 17), so the brakes push along the
 *   path; a tap mid-corner still tucks the nose in (8 to 9 degrees, as before). Holding it to 3.4 degrees ("tap the
 *   brakes mid corner, and that makes it go straight") was tried first;
 * - a tyre past its peak slip angle brakes less (to 40% at twice the peak), keeping the grip to turn with. Alone, the
 *   wider limit still let the 2004 turn 4 to 9 degrees against the steering; with no floor, a car braking at full
 *   lock went round in circles instead of stopping.
 * Braking and turning, it now slides 8 to 10 degrees at most (from 21 to 25) and never turns against the steering;
 * a tap mid-corner runs 0.7 to 1.9 m wide (from 3 to 4.7). Straight-line stops are exactly as before. Corner lab
 * (tomorrow's 2022 circuit, four slow corners; the 2004's next, four): clean times the same, drifting slower
 * everywhere. Bench beginners do better on both cars (clean laps of 360: 2022 251 from 231, 2004 196 from 165; no
 * spins). Easing the rear brakes whenever the car turns (as a real car's brake distribution does) also fixed the
 * slide, but stopped the car later while steering, and bench beginners overshot (2004 tidy keys 63 clean of 72 down
 * to 13 to 51); a stronger stability control while braking didn't hold it.
 */
export const HANDLING_35_FROM = '2026-10-14';
const ERA_PHYSICS_35: Partial<Record<EraId, Partial<PhysicsTuning>>> = {
  '2004': { slipAssistBraking: 0.12, absSliding: 1, absSlidingFloor: 0.4 },
  '2022': { slipAssistBraking: 0.12, absSliding: 1, absSlidingFloor: 0.4 },
};

/** The car's physics on a date (no date: the current handling). */
export function eraPhysics(era: EraId, date?: string): PhysicsTuning {
  const base = { ...DEFAULT_PHYSICS, ...ERA_PHYSICS[era] };
  if (date !== undefined && date < HANDLING_2_FROM) return base;
  const second = { ...base, ...ERA_PHYSICS_2[era] };
  if (date !== undefined && date < HANDLING_3_FROM) return second;
  const third = { ...second, ...HANDLING_3_ALL, ...ERA_PHYSICS_3[era] };
  if (date !== undefined && date < HANDLING_31_FROM) return third;
  const thirtyOne = { ...third, ...HANDLING_31_ALL };
  if (date !== undefined && date < HANDLING_32_FROM) return thirtyOne;
  const thirtyTwo = { ...thirtyOne, ...ERA_PHYSICS_32[era] };
  if (date !== undefined && date < HANDLING_34_FROM) return thirtyTwo;
  const thirtyFour = { ...thirtyTwo, ...ERA_PHYSICS_34[era] };
  if (date !== undefined && date < HANDLING_33_FROM) return thirtyFour;
  const thirtyThree = { ...thirtyFour, ...ERA_PHYSICS_33[era] };
  if (date !== undefined && date < HANDLING_35_FROM) return thirtyThree;
  return { ...thirtyThree, ...ERA_PHYSICS_35[era] };
}

/**
 * The handling passes, in the order eraPhysics lays them on, and the cars each changed ('all': every car). Only for
 * naming them (the stats page's "handling 3.4"): the physics are eraPhysics's alone.
 */
const HANDLING_PASSES: [name: string, from: string, cars: Partial<Record<EraId, Partial<PhysicsTuning>>> | 'all'][] = [
  ['2', HANDLING_2_FROM, ERA_PHYSICS_2],
  ['3', HANDLING_3_FROM, 'all'],
  ['3.1', HANDLING_31_FROM, 'all'],
  ['3.2', HANDLING_32_FROM, ERA_PHYSICS_32],
  ['3.4', HANDLING_34_FROM, ERA_PHYSICS_34],
  ['3.3', HANDLING_33_FROM, ERA_PHYSICS_33],
  ['3.5', HANDLING_35_FROM, ERA_PHYSICS_35],
];

/** The handling a car raced with on a date: the last pass that changed it by then ('1': none had yet). */
export function handlingOf(era: EraId, date: string): string {
  let name = '1';
  for (const [n, from, cars] of HANDLING_PASSES) if (date >= from && (cars === 'all' || Object.keys(cars[era] ?? {}).length)) name = n;
  return name;
}

/**
 * The cars a day's laps may also have been driven with: on the day a handling pass came in mid-race (6
 * October 2026), the handling the day began with. Those laps stay on the board and replay with their own car.
 * (Not 12 October: the old 2004's laps were taken off that day's board.)
 */
export function earlierPhysics(era: EraId, date: string): PhysicsTuning[] {
  return date === HANDLING_3_FROM ? [eraPhysics(era, '2026-10-05')] : [];
}

/** Slider ranges for the dev panel. */
export const PHYSICS_RANGES: Partial<Record<keyof PhysicsTuning, [number, number, number]>> = {
  mass: [400, 1000, 10],
  yawInertia: [400, 1600, 10],
  cgToFront: [0.7, 1.6, 0.01],
  cgToRear: [0.7, 1.6, 0.01],
  cgHeight: [0.1, 0.8, 0.01],
  tireMu: [0.8, 2.0, 0.01],
  frontGrip: [0.7, 1.2, 0.01],
  rearGrip: [0.7, 1.2, 0.01],
  peakSlipFront: [0.04, 0.3, 0.005],
  peakSlipRear: [0.04, 0.3, 0.005],
  falloffFront: [0, 0.6, 0.01],
  falloffRear: [0, 0.6, 0.01],
  lockGrip: [0.4, 1, 0.01],
  lockLateral: [0, 1, 0.01],
  wheelspinGrip: [0.4, 1, 0.01],
  frictionEllipse: [1, 1.6, 0.01],
  enginePeakTorque: [100, 600, 5],
  finalDrive: [2.5, 6, 0.05],
  brakeForce: [3000, 16000, 100],
  brakeBias: [0.4, 0.8, 0.01],
  pedalRise: [1, 30, 0.5],
  pedalRelease: [1, 40, 0.5],
  downforceFront: [0, 1, 0.01],
  downforceRear: [0, 1, 0.01],
  dragCoef: [0.1, 1.5, 0.01],
  rollingResistance: [0, 60, 1],
  steerRate: [1, 12, 0.1],
  steerReturnRate: [1, 20, 0.1],
  maxSteerAngle: [0.2, 0.9, 0.01],
  steerLimitMargin: [0.8, 2.5, 0.01],
  steerOverdrive: [0, 0.15, 0.005],
  curbGrip: [0.5, 1.1, 0.01],
  curbDrag: [0, 3, 0.05],
  grassGrip: [0.2, 1, 0.01],
  grassDrag: [0, 10, 0.1],
  gravelGrip: [0.1, 1, 0.01],
  gravelDrag: [0, 15, 0.1],
  barrierRestitution: [0, 0.8, 0.01],
  barrierFriction: [0, 1, 0.01],
  tractionControl: [0, 1, 0.05],
  tcTarget: [0.5, 1.2, 0.01],
  tcCornering: [0, 1, 0.01],
  abs: [0, 1, 0.05],
  absTarget: [0.5, 1.1, 0.01],
  yawAssist: [0, 20, 0.5],
  slipAssistAngle: [0, 1, 0.01],
  absCornering: [0, 1, 0.05],
  absCorneringTop: [0, 60, 1],
  aeroYawLoss: [0, 10, 0.25],
  escBrake: [0, 40, 1],
  escMaxDecel: [0, 15, 0.5],
  slipAssistStraighten: [0, 1, 1],
  slipAssistBraking: [0, 0.3, 0.01],
  absSliding: [0, 3, 0.1],
  absSlidingFloor: [0, 1, 0.05],
};

export function gearRatios(p: PhysicsTuning): number[] {
  return [p.gear1, p.gear2, p.gear3, p.gear4, p.gear5];
}

/** Engine torque multiplier vs rpm / redline (piecewise linear). */
export const TORQUE_CURVE: ReadonlyArray<readonly [number, number]> = [
  [0.0, 0.45],
  [0.25, 0.62],
  [0.45, 0.84],
  [0.65, 1.0],
  [0.82, 0.97],
  [1.0, 0.84],
];

export const TRACK = {
  version: 1,
  /** Layout size for every car. Tracks don't scale with the day's car, so faster eras lap faster. */
  layoutScale: 1.15,
  halfWidth: 6,
  curbWidth: 1.3,
  sampleSpacing: 2,
  /** Grass runoff each side, metres beyond the asphalt edge. */
  runoffGrass: 8.5,
  /** Gravel trap depth on the outside of braking zones. */
  runoffGravel: 22,
  runoffMin: 4.5,
  /** Barriers never come closer than this to the asphalt edge. */
  barrierMargin: 1.5,
  /** Minimum centerline radius anywhere, metres. */
  minRadius: 14,
  /** Parts of the circuit further apart than this along the track must keep clearance. */
  clearanceArc: 70,
  minClearance: 24,
  curbRadius: 95,
  lapTimeMin: 30,
  lapTimeMax: 45,
  maxAttempts: 400,
  spawnDistance: 70,
  spawnSpeed: 36,
  // --- feature thresholds
  straightMaxCurvature: 1 / 330,
  longStraightMin: 150,
  heavyBrakingDrop: 22,
  hairpinAngle: 2.4,
  hairpinMaxRadius: 32,
  chicaneMinAngle: 0.25,
  /** Each chicane lobe turns at most this much (radians, within 30 m of its apex)... */
  chicaneMaxLobe: 1.2,
  /** ...and isn't hairpin-tight. */
  chicaneMinRadius: 18,
  /** Each lobe of a chicane is tighter than this radius... */
  chicaneMaxRadius: 70,
  /** ...and the two apexes are at most this far apart. */
  chicaneMaxGap: 60,
  sweeperMinAngle: 0.6,
  sweeperMinRadius: 55,
  sweeperMaxRadius: 260,
  cornerCurvature: 1 / 240,
};

/**
 * Generator v3 (circuits from 2026-09-29 on): layouts built from corner
 * modules, some of them figures of eight. Earlier days keep TRACK's rules exactly.
 */
export const TRACK_V3 = {
  lapTimeMin: 30,
  lapTimeMax: 50,
  minLength: 950,
  maxLength: 1950,
  /** Share of days drawn as a figure of eight (before validation). */
  figure8Share: 0.25,
  /** The underpass: how far the lower road drops (road to road), its steepest grade. */
  crossingDepth: 6.4,
  crossingGrade: 0.09,
  /** The two passes cross at no shallower than this (sine of the angle between them). */
  crossingMinSin: 0.78,
  /** Cutting walls and bridge parapets, from each road's centreline. */
  cutHalf: 9.2,
  deckHalf: 7.4,
  /** The main straight can be shorter than v2's, and its braking a little lighter: less straight, more corners. */
  longStraightMin: 125,
  heavyBrakingDrop: 18,
  /** Street sections run this close to their walls; a banked curve's wall stands this far beyond the road on the high side. */
  streetRunoff: 2.4,
  bankRunoff: 1.4,
  /** Steepest grade anywhere (set pieces can be steep: this is a cartoon). */
  maxGrade: 0.24,
  /** Alternating bends counted as esses: each at least this radius and this turn (radians). */
  essesMinRadius: 28,
  essesMinAngle: 0.5,
};

export const BOT = {
  /** Grip fractions the bot tries, from careful to committed. */
  gripLevels: [0.9, 0.96, 1.01, 1.05],
  brakeDecel: 12.5,
  lookaheadBase: 6,
  lookaheadPerSpeed: 0.42,
  lineMargin: 1.15,
  throttleGain: 0.45,
  brakeGain: 0.35,
  maxTicks: 60 * 75,
};

/**
 * Grip and drag of the runoff surfaces added on 30 September 2026 (they only
 * exist on circuits from then on). Part of the simulation: changing them
 * changes lap results on those days, so bump SIM_VERSION if you do.
 */
export const RUNOFF_SURFACES = {
  /** Artificial turf / packed earth: a wheel on it costs a little, never a spin. */
  turfGrip: 0.9,
  turfDrag: 0.3,
  /** Painted tarmac runoff: barely different from the track itself. */
  tarmacGrip: 0.96,
  tarmacDrag: 0.05,
};

/** Medal targets as multiples of the bot's (author) time. */
export const MEDALS = {
  gold: 1.055,
  silver: 1.115,
  bronze: 1.24,
};

export const CAMERA = {
  // Above the driver's big helmet (its top is ~1.85 m), so the road ahead
  // stays visible over it; pitched ~6 degrees down.
  distance: 5.3,
  height: 2.6,
  lookAhead: 9,
  lookHeight: 1.15,
  // The view widens with speed (squared, full at fovSpeed m/s) and the camera comes in closer and lower: the road
  // rushes in (a Reddit player on a computer had no sense of speed, 10 Oct 2026). All of it eased in with speed.
  fovBase: 56,
  fovMax: 80,
  fovSpeed: 60,
  /** m closer per m/s of speed (up to 60 m/s: 0.85 m). Until 10 Oct 2026 it drifted back instead (0.006 m per m/s). */
  speedPull: 0.85 / 60,
  /** m lower at 60 m/s (squared with speed). */
  speedSink: 0.38,
  /**
   * At speed (squared, full at 60 m/s) the camera shakes finely and at random, like a car on a real road: it nods
   * about this much (rad) and turns about this much (rad), a pixel or so, surging and easing, with a knock now and
   * then (camera.ts Wobble); never a roll. A slow sway
   * was tried first and could make people queasy. Off with the pause poster's "screen shake" switch (which stills
   * the bob and the jolt too) and for anyone whose system asks for less motion.
   */
  shakePitch: 0.0025,
  shakeYaw: 0.0019,
  yawStiffness: 7.5,
  slipLook: 0.35,
  distanceSpring: 5,
  accelPull: 0.05,
  /** Grass's bob (m, up and down about twice a second). */
  bumpGrass: 0.035,
  /** A wall hit's throw towards the car: its speed (m/s) per m/s into the wall, at most `joltMax`; the spring that
   * brings it back (rad/s, about 1.6 times a second, settling with barely an overshoot); no new throw sooner. */
  jolt: 0.35,
  joltMax: 4,
  joltSpring: 10,
  joltGap: 0.7,
};
